import React, { useEffect, lazy, Suspense, useRef } from "react";
import "./App.css";
import { Routes, Route } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import Account from "./pages/account";
import Login from "./pages/auth/login";
import { GlobalActionTypes } from "./Providers/appProvider/reducers/globalReducer";
import { authTokenPayload } from "./config/constant";
import { useAppProvider } from "./Providers/appProvider/AppProvider";
import { AppstoreApi, getApiKey } from "./api";
import Authenticator from "./pages/Authenticator";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import Layout from "./layouts/layout";
import Signup from "./pages/auth/signup";

const AppDetail = lazy(() => import("./pages/appDetail"));
const AppListPage = lazy(() => import("./pages/appList"));
const Redirect = lazy(() => import("./components/Redirect"));
const ShopIQInstallGuide = lazy(() =>
  import("./pages/shopIQGuide/shopIQInstallGuide")
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const clientId = `676930511829-1nrvm0ngccabg7j821ehmrb30furrubo.apps.googleusercontent.com`;

  const { state, dispatch } = useAppProvider();
  const {
    globalReducer: { forumAdminApiKey, pricingPlans },
  } = state;

  const ref = useRef(false);

  const getForumAdminApiKey = async () => {
    const payload = authTokenPayload({ userid: "forum_admin" });
    const res = await getApiKey(payload);

    dispatch({
      type: GlobalActionTypes.SET_FORUM_ADMIN_API_KEY,
      payload: res?.apikey,
    });

    sessionStorage.setItem("forum_admin_api_key", JSON.stringify(res?.apikey));
  };

  useEffect(() => {
    // Create the first inline script element
    const inlineScript = document.createElement("script");
    inlineScript.type = "text/javascript";
    inlineScript.innerHTML = `
      var _paq = (window._paq = window._paq || []);
_paq.push(['enableLinkTracking'], ['enableHeartBeatTimer', 30], ['trackAllContentImpressions']);
(function () {
var u = 'https://leads.bangdb.com:18080/stream/bangdb_ClickStream/event';
_paq.push(['setTrackerUrl', u], ['setSiteId', 'bangdb']);
})();
var _mtm = window._mtm = window._mtm || [];
_mtm.push({'mtm.startTime': (new Date().getTime()), 'event': 'mtm.Start'});
    `;

    // Append the inline script to the head
    document.head.appendChild(inlineScript);

    // Create the first async script element
    const asyncScript1 = document.createElement("script");
    asyncScript1.src = "https://cdn.bangdb.com/tm/HWFXJJTCDNWL/matomo.js";
    asyncScript1.async = true;

    // Append the first async script to the head
    document.head.appendChild(asyncScript1);

    // Create the second async script element
    const asyncScript2 = document.createElement("script");
    asyncScript2.src =
      "https://cdn.bangdb.com/tm/HWFXJJTCDNWL/166dfd67ac054c13be61d9f6625c9172/mastercontainer.js";
    asyncScript2.async = true;

    // Append the second async script to the head
    document.head.appendChild(asyncScript2);

    // Cleanup function to remove scripts if the component unmounts
    return () => {
      document.head.removeChild(inlineScript);
      document.head.removeChild(asyncScript1);
      document.head.removeChild(asyncScript2);
    };
  }, []);

  useEffect(() => {
    if (!forumAdminApiKey && !sessionStorage.getItem("userInfo")) {
      if (!ref.current) {
        getForumAdminApiKey();
      }
    }

    return () => {
      ref.current = true;
    };
  }, []);

  return (
    <GoogleOAuthProvider clientId={clientId}>
      <QueryClientProvider client={queryClient}>
        <Layout>
          <Routes>
            <Route exact path="/auth/login" element={withSuspese(<Login />)} />
            <Route
              exact
              path="/auth/signup"
              element={withSuspese(<Signup render_type={"page"} />)}
            />

            <Route exact path="/" element={withSuspese(<AppListPage />)} />

            <Route exact path="/account" element={withSuspese(<Account />)} />

            <Route path="/admin" element={withSuspese(<AppListPage />)} />

            <Route path="/detail/:appid" element={withSuspese(<AppDetail />)} />
            <Route path="/auth" element={withSuspese(<Authenticator />)} />
            <Route
              path="/shopiq/installguide"
              element={withSuspese(<ShopIQInstallGuide />)}
            />

            <Route path="/redirect" element={withSuspese(<Redirect />)} />
          </Routes>
        </Layout>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  );
}

function withSuspese(children, fallback = null) {
  return <Suspense fallback={<div>{fallback}</div>}>{children}</Suspense>;
}

export default App;
